// Pure (no-React) validation for the mobile Collaborator view's Actual Start /
// Actual Finish edits. Mirrors the server-side rules enforced on
// PUT /activities/{id}/progress so the user sees the limit BEFORE a save trip:
//   - actual_finish >= actual_start          (finish can't precede start)
//   - actual_finish requires actual_start     (no finish without a start)
//   - neither actual may be after today NOR after data_date (ceiling =
//     min(today, data_date)): actuals are recorded fact, never a forecast —
//     mirrors the desktop picker (ActualDatePickerBody.min(today, data_date))
//
// All date inputs are date-only ISO strings "YYYY-MM-DD" (or null/undefined).
// We compare strings lexicographically — for well-formed "YYYY-MM-DD" that is
// identical to chronological order and avoids any timezone parsing. Malformed
// inputs are GUARDED: a value that is not a clean "YYYY-MM-DD" is treated as
// "absent" for ordering so we never emit a bogus error off garbage.

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Normalize to a clean "YYYY-MM-DD" for comparison, or null if absent/malformed.
// Tolerates a trailing time component ("2026-05-17T00:00:00") by taking the
// date part, matching how the server serializes some date fields.
export function normalizeDateOnly(value) {
  if (value == null) return null;
  const dateOnly = String(value).split("T")[0].trim();
  return ISO_DATE_RE.test(dateOnly) ? dateOnly : null;
}

// a > b ? Both must already be normalized (clean ISO) or null. Returns false if
// either side is null (no meaningful ordering against an absent date).
function isAfter(a, b) {
  if (!a || !b) return false;
  return a > b;
}

// ── Individual rule helpers (exported for focused testing) ──────────────────

// Rule (a): finish must not be before start. Error attaches to actualFinish.
export function finishNotBeforeStart(actualStart, actualFinish) {
  const start = normalizeDateOnly(actualStart);
  const finish = normalizeDateOnly(actualFinish);
  if (start && finish && finish < start) {
    return "Actual Finish can't be before Actual Start";
  }
  return null;
}

// Rule (b): finish requires a start. Error attaches to actualFinish.
export function finishRequiresStart(actualStart, actualFinish) {
  const start = normalizeDateOnly(actualStart);
  const finish = normalizeDateOnly(actualFinish);
  if (finish && !start) {
    return "Set Actual Start first";
  }
  return null;
}

// Rule (c): a single actual value must not be after the data date.
// Returns an error string (for the offending field) or null. dataDate optional.
export function notAfterDataDate(value, dataDate) {
  const v = normalizeDateOnly(value);
  const dd = normalizeDateOnly(dataDate);
  if (v && dd && isAfter(v, dd)) {
    return "Can't be after the project data date";
  }
  return null;
}

// Rule (d): a single actual value must not be after today (an actual is a
// recorded fact, never a future forecast). Returns an error string or null.
export function notAfterToday(value, today) {
  const v = normalizeDateOnly(value);
  const t = normalizeDateOnly(today);
  if (v && t && isAfter(v, t)) {
    return "Can't be after today";
  }
  return null;
}

// ── Aggregate validator ─────────────────────────────────────────────────────
// warn+block: ok=false whenever any error exists. Each field carries at most
// one error (first violated rule wins per field).
export function validateActualDates({ actualStart, actualFinish, dataDate, today } = {}) {
  const errors = {};

  // Effective ceiling = the EARLIER of today and the data date (actuals are
  // recorded fact, never future, and never beyond the data date). Mirrors the
  // desktop picker's min(today, data_date). The earlier bound owns the message.
  // `today` is injectable so tests are deterministic; defaults to the real today.
  const t = normalizeDateOnly(today) || todayISO();
  const dd = normalizeDateOnly(dataDate);
  const dataDateBinds = dd && dd < t;
  const ceilingErr = (value) =>
    dataDateBinds ? notAfterDataDate(value, dd) : notAfterToday(value, t);

  // actual_start: only the ceiling applies.
  const startErr = ceilingErr(actualStart);
  if (startErr) errors.actualStart = startErr;

  // actual_finish: requires-start > before-start > ceiling.
  const requiresStartErr = finishRequiresStart(actualStart, actualFinish);
  const beforeStartErr = finishNotBeforeStart(actualStart, actualFinish);
  const finishErr = requiresStartErr || beforeStartErr || ceilingErr(actualFinish);
  if (finishErr) errors.actualFinish = finishErr;

  return { ok: Object.keys(errors).length === 0, errors };
}

// Local-date "YYYY-MM-DD" for the "Today" shortcut. Uses LOCAL calendar parts
// (not toISOString, which is UTC and can roll the day in non-UTC zones). Accepts
// an injected Date so tests are deterministic.
export function todayISO(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default validateActualDates;
